import type { BaseEnvelope, BasePatchOp } from './base-patch';
import type { AppliedFrame, AppliedNewEntity, AppliedTouchedRow } from '../transport/applied-frame';
import { fromBaseRecord } from './from-base-context';
import { readItemField } from './item-field-reader';
import { _ftCamField } from '../../shared/campaign-field';
import { relocationColumnsOnly, savedCreatureRelocationFrame } from './saved-creature-relocation';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const columns = new Set(['item_id', 'attached_campaign_id', 'campaign_invite_code', 'player_character_inactive']);

/** Membership/attendance are saved display inputs. Mixed character-rule writes keep their native lane. */
export function partyPlayerOps(ops: readonly BasePatchOp[]): { rows: AppliedTouchedRow[]; remaining: BasePatchOp[] } | null {
    const players = ops.filter(op => op.table === 'player_characters');
    if (!players.length || players.some(op => op.op !== 'upsert' || op.inputs || op.catalog_inputs
        || Object.keys(op.cols).some(key => !columns.has(key)))) return null;
    return { rows: players.map(op => ({ table: op.table, row_id: op.row_id, row: op.cols, partial: true })),
        remaining: ops.filter(op => op.table !== 'player_characters') };
}

/** A join also saves its browser-selected arrival. Compose the existing relocation diff with the PC row. */
export function savedPartyPlayerFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[], arrivals: readonly AppliedNewEntity[],
    computed: unknown, unavailable: ReadonlySet<string>): AppliedFrame | null {
    const party = partyPlayerOps(ops);
    if (!party) return null;
    let frame: AppliedFrame;
    if (!party.remaining.length || envelope.entity === 'inventory'
        && party.remaining.every(op => op.table === 'encounter_creatures' && relocationColumnsOnly(op.cols))) {
        frame = { kind: 'applied', operation_id: `party-player:${envelope.entity}:${envelope.entity_id}:${ops[0]!.base_rev}`,
            encounter_id: envelope.entity_id, actor_id: '', rev: 0, member_revs: {}, rows: [], computed: {}, new_entities: [], deletes: [] };
    } else {
        const relocation = savedCreatureRelocationFrame(envelope, party.remaining, arrivals, computed, unavailable);
        if (!relocation) return null;
        frame = relocation;
    }
    return { ...frame, rows: [...frame.rows, ...party.rows] };
}

/** Update the held campaign index from the committed PC delta. Retain its array and every unrelated row. */
export function patchCampaignPartyPlayers(campaign: Row, ops: readonly BasePatchOp[], members: readonly Row[],
    revisions: Map<string, number>): boolean {
    const campaignId = upper(_ftCamField(campaign, 'campaign_id'));
    if (!campaignId) return false;
    let changed = false;
    const roster = Array.isArray(campaign.campaign_players) ? campaign.campaign_players as Row[] : [];
    for (const op of ops) {
        if (op.table !== 'player_characters') continue;
        const id = upper(op.row_id);
        const revision = op.dependency_revs?.[id] ?? (upper(op.entity_id) === id ? op.base_rev : undefined);
        if (revision === undefined || revision <= (revisions.get(id) ?? -1)) continue;
        const at = roster.findIndex(row => upper(readItemField(row, 'item_id')) === id);
        if (op.op === 'delete') {
            if (at >= 0) { roster.splice(at, 1); changed = true; }
            revisions.set(id, revision);
            continue;
        }
        const held = at >= 0 ? roster[at] : undefined;
        const member = members.find(row => fromBaseRecord(row.player_characters)
            && upper(readItemField(row.player_characters, 'item_id')) === id);
        const pc = member && fromBaseRecord(member.player_characters) ? member.player_characters : null;
        const hasMembership = Object.prototype.hasOwnProperty.call(op.cols, 'attached_campaign_id');
        if (hasMembership && upper(readItemField(op.cols, 'attached_campaign_id')) !== campaignId) {
            if (at >= 0) { roster.splice(at, 1); changed = true; }
        } else if (held) {
            for (const key of ['player_character_inactive', 'player_name', 'permissioned']) if (key in op.cols
                && String(readItemField(held, key) ?? '') !== String(readItemField(op.cols, key) ?? '')) {
                held[key] = readItemField(op.cols, key); changed = true;
            }
        } else if (hasMembership && pc) {
            const row: Row = {};
            // Same lean roster inputs as the campaign SELECT; no account fields or character-rule subtree.
            for (const key of ['item_id', 'player_name', 'player_character_created_by_id', 'player_character_inactive',
                'permissioned', 'player_username', 'experience_marks']) {
                const value = key in op.cols ? readItemField(op.cols, key) : readItemField(pc, key);
                if (value !== null && value !== undefined && value !== '') row[key] = value;
            }
            roster.push(row); changed = true;
        } else if (hasMembership) continue; // A cold arrival is installed before its roster notification.
        revisions.set(id, revision);
    }
    if (changed && !Array.isArray(campaign.campaign_players)) campaign.campaign_players = roster;
    return changed;
}
